import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Alert, Form, Button } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import ButtonWithLoad from "@common/components/ButtonWithLoad/ButtonWithLoad";
import Divider from "@common/components/Divider/Divider";
import { requestPermission } from "@common/components/GlobalNotificationDispatcher/GlobalNotificationDispatcher";
import * as userActions from "@profile/actions/userActions";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from "@profile/services/userService";
import "./NotificationSettings.scss";

const NotificationSettings = () => {
  const dispatch = useDispatch();
  const pushPermissionGranted = useSelector(
    (state) => state.userInfo?.notifications ?? false,
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState(false);

  const [preferences, setPreferences] = useState({
    email_enabled: true,
    email_user_action: true,
    email_machine_error: true,
    email_ingredient_reminder: true,
    email_stage_update: true,
    push_enabled: true,
    push_user_action: true,
    push_machine_error: true,
    push_ingredient_reminder: true,
    push_stage_update: true,
  });

  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const response = await getNotificationPreferences();
      const data = response && response.data ? response.data : response;
      if (data) {
        setPreferences({
          email_enabled: data.email_enabled !== false,
          email_user_action: data.email_user_action !== false,
          email_machine_error: data.email_machine_error !== false,
          email_ingredient_reminder: data.email_ingredient_reminder !== false,
          email_stage_update: data.email_stage_update !== false,
          push_enabled: data.push_enabled !== false,
          push_user_action: data.push_user_action !== false,
          push_machine_error: data.push_machine_error !== false,
          push_ingredient_reminder: data.push_ingredient_reminder !== false,
          push_stage_update: data.push_stage_update !== false,
        });
      }
    } catch (err) {
      setErrorMessage(
        "Unable to load notification preferences. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleChange = (key) => (e) => {
    const checked = e.target.checked;
    setSuccessMessage(false);
    setErrorMessage("");
    setPreferences((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const allActive = preferences.email_enabled || preferences.push_enabled;

  const handleToggleAll = useCallback(async () => {
    setSuccessMessage(false);
    setErrorMessage("");
    const targetState = !allActive;

    const updated = {
      ...preferences,
      email_enabled: targetState,
      push_enabled: targetState,
    };
    setPreferences(updated);

    if (targetState) {
      requestPermission();
    } else {
      if (window.OneSignal?.User?.PushSubscription) {
        try {
          await window.OneSignal.User.PushSubscription.optOut();
        } catch (err) {
          console.error("OneSignal optOut failed:", err);
        }
      }
      dispatch(userActions.setNotificationPermission(false));
    }

    try {
      setSaving(true);
      await updateNotificationPreferences(updated);
      setSuccessMessage(true);
    } catch (err) {
      setErrorMessage("Failed to update preferences. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [allActive, preferences, dispatch]);

  const handleSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    try {
      setSaving(true);
      setErrorMessage("");
      setSuccessMessage(false);

      if (preferences.push_enabled && !pushPermissionGranted) {
        requestPermission();
      }

      await updateNotificationPreferences(preferences);
      setSuccessMessage(true);
    } catch (err) {
      setErrorMessage("Failed to update preferences. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <span className="ion-load-c spin">
          {" "}
          Loading notification preferences...
        </span>
      </div>
    );
  }

  const {
    email_enabled,
    email_user_action,
    email_machine_error,
    email_ingredient_reminder,
    email_stage_update,
    push_enabled,
    push_user_action,
    push_machine_error,
    push_ingredient_reminder,
    push_stage_update,
  } = preferences;

  const categories = [
    {
      id: "user_action",
      title: "User Actions Required",
      desc: "Physical interactions like adding hops, water, removing malt pipe, and valve handoffs.",
      emailKey: "email_user_action",
      pushKey: "push_user_action",
      emailChecked: email_user_action,
      pushChecked: push_user_action,
    },
    {
      id: "machine_error",
      title: "Machine Errors & Warnings",
      desc: "Immediate alerts for pump jams, flow stoppage, or temperature warnings.",
      emailKey: "email_machine_error",
      pushKey: "push_machine_error",
      emailChecked: email_machine_error,
      pushChecked: push_machine_error,
    },
    {
      id: "ingredient_reminder",
      title: "10-Minute Advance Hop Reminders",
      desc: "Advance notification ~10 minutes before the carousel drops hops into the kettle.",
      emailKey: "email_ingredient_reminder",
      pushKey: "push_ingredient_reminder",
      emailChecked: email_ingredient_reminder,
      pushChecked: push_ingredient_reminder,
    },
    {
      id: "stage_update",
      title: "Brew & Fermentation Milestones",
      desc: "Major phase updates (boiling started, wort chilled, fermentation stage 2, cleaning done).",
      emailKey: "email_stage_update",
      pushKey: "push_stage_update",
      emailChecked: email_stage_update,
      pushChecked: push_stage_update,
    },
  ];

  return (
    <div className="notification-settings">
      <Row>
        <Col sm={12} md={10} mdOffset={1}>
          <div className="notification-settings__header">
            <h3>Notification Preferences</h3>
            <p>
              Manage how and when MiniBrew alerts you during brewing,
              fermentation, and maintenance.
            </p>
          </div>
          <Divider />

          {/* Quick Master On/Off Banner */}
          <div
            className={`notification-settings__quick-banner ${allActive ? "active" : "inactive"}`}
          >
            <div className="banner-left">
              <div className="bell-icon-wrapper">
                <i
                  className={
                    allActive ? "ion-ios-bell" : "ion-ios-bell-outline"
                  }
                />
              </div>
              <div className="banner-text">
                <div className="banner-title">
                  All Notifications:{" "}
                  <strong>{allActive ? "Active" : "Paused"}</strong>
                </div>
                <div className="banner-subtext">
                  {allActive
                    ? "You will receive alerts according to your customized preferences below."
                    : "All email and push notifications are currently silenced."}
                </div>
              </div>
            </div>
            <div className="banner-right">
              <Button
                className={`quick-toggle-btn ${allActive ? "btn-danger" : "btn-warning"}`}
                onClick={handleToggleAll}
                disabled={saving}
              >
                {allActive ? "Mute All" : "Enable All"}
              </Button>
            </div>
          </div>

          {errorMessage && (
            <Alert bsStyle="danger">
              <span className="ion-alert-circled"> {errorMessage}</span>
            </Alert>
          )}

          {successMessage && (
            <Alert bsStyle="success">
              <span className="checkmark-circle">
                {" "}
                Notification preferences updated successfully.
              </span>
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Channel Master Switches */}
            <div className="notification-settings__channels-card">
              <h4 className="section-subtitle">Notification Channels</h4>
              <div className="channels-grid">
                <div className="channel-box">
                  <div className="channel-box__toggle">
                    <input
                      type="checkbox"
                      id="email_enabled"
                      checked={email_enabled}
                      onChange={handleChange("email_enabled")}
                    />
                  </div>
                  <div className="channel-box__content">
                    <label htmlFor="email_enabled" className="channel-title">
                      📧 Email Notifications
                    </label>
                    <div className="channel-desc">
                      Transactional emails delivered directly to your registered
                      inbox.
                    </div>
                  </div>
                </div>

                <div className="channel-box">
                  <div className="channel-box__toggle">
                    <input
                      type="checkbox"
                      id="push_enabled"
                      checked={push_enabled}
                      onChange={handleChange("push_enabled")}
                    />
                  </div>
                  <div className="channel-box__content">
                    <label htmlFor="push_enabled" className="channel-title">
                      📱 Push Notifications
                    </label>
                    <div className="channel-desc">
                      Instant lock screen alerts on your phone or web browser.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Granular Matrix Table */}
            <div className="notification-settings__matrix-card">
              <h4 className="section-subtitle">Alert Categories</h4>
              <p className="matrix-instruction">
                Select which alerts to receive across each channel.
              </p>

              <div className="matrix-table">
                <div className="matrix-header">
                  <div className="col-category">Category</div>
                  <div className="col-channel">📧 Email</div>
                  <div className="col-channel">📱 Push</div>
                </div>

                {categories.map((cat) => (
                  <div key={cat.id} className="matrix-row">
                    <div className="col-category">
                      <div className="cat-title">{cat.title}</div>
                      <div className="cat-desc">{cat.desc}</div>
                    </div>
                    <div
                      className={`col-channel ${!email_enabled ? "disabled" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={cat.emailChecked}
                        onChange={handleChange(cat.emailKey)}
                        disabled={!email_enabled}
                        title={
                          email_enabled
                            ? `Toggle Email for ${cat.title}`
                            : "Email notifications are disabled"
                        }
                      />
                    </div>
                    <div
                      className={`col-channel ${!push_enabled ? "disabled" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={cat.pushChecked}
                        onChange={handleChange(cat.pushKey)}
                        disabled={!push_enabled}
                        title={
                          push_enabled
                            ? `Toggle Push for ${cat.title}`
                            : "Push notifications are disabled"
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="notification-settings__actions">
              <ButtonWithLoad
                type="submit"
                bsStyle="primary"
                isLoading={saving}
              >
                Save Preferences
              </ButtonWithLoad>
            </div>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default NotificationSettings;
